import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MostPopular = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchPopular = async () => {
      try {
        const res = await axios.get(`${API}/services/popular`);
        // Pick top ~20 diverse services across categories
        const diverse = [];
        const catCounts = {};
        for (const svc of res.data) {
          const catCount = catCounts[svc.category] || 0;
          if (catCount < 3) {
            diverse.push(svc);
            catCounts[svc.category] = catCount + 1;
          }
          if (diverse.length >= 24) break;
        }
        setServices(diverse);
      } catch (e) {
        console.error('Error fetching popular services:', e);
      }
    };
    fetchPopular();
  }, []);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    return () => { if (el) el.removeEventListener('scroll', checkScroll); };
  }, [services]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -300 : 300,
        behavior: 'smooth'
      });
    }
  };

  if (services.length === 0) return null;

  return (
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">
            Most Popular Services
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-200 ${
                canScrollLeft
                  ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-100 text-gray-700'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-200 ${
                canScrollRight
                  ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-100 text-gray-700'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {services.map((cat, idx) => (
            <a key={idx} href={`/service/${cat.slug}`} className="flex-shrink-0 group no-underline" style={{ scrollSnapAlign: 'start' }}>
              <div className="w-[180px]">
                <div className="w-[180px] h-[180px] rounded-xl overflow-hidden mb-3 relative">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300" />
                </div>
                <p className="text-gray-900 text-sm font-semibold group-hover:text-[#37b34a] transition-colors duration-200">
                  {cat.name}
                </p>
                <p className="text-gray-400 text-xs mt-0.5">{cat.category}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MostPopular;
